// import { createClient } from "@supabase/supabase-js"; // Remove direct creation
import { createClient } from "@/utils/supabase/client"; // Import the new client utility
import type { GameType } from "./types";
import { fetchGames } from "./games";

// const supabase = createClient(process.env.NEXT_PUBLIC_SUPABASE_URL!, process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!); // Remove direct creation

interface Vector {
  speed: number;
  mood: number;
  complexity: number;
  male_focus: number;
  female_focus: number;
  casualness: number;
  playtime: number;
  visual: number;
  theme: number;
  depth: number;
}

interface CategoryVector {
  category: string;
  vector: Vector;
  description: Record<keyof Vector, string>;
}

// Helper function to safely handle game categories
function getGameCategories(categories: unknown): string[] {
  if (Array.isArray(categories)) {
    // Filter out any non-string or empty string values just in case
    return categories.filter((c): c is string => typeof c === 'string' && c.trim() !== '');
  } else if (typeof categories === 'string') {
    return categories.split(',').map((c: string) => c.trim()).filter(c => c !== '');
  }
  return [];
}

/**
 * SQL 스키마 정의 (Supabase SQL 에디터에서 실행)
 * 
 * -- 카테고리 벡터 테이블 (각 카테고리별 10개 속성의 벡터값 저장)
 * CREATE TABLE category_vectors (
 *   category TEXT PRIMARY KEY,
 *   vector JSONB NOT NULL, -- 예: { "speed": 70, "mood": 30, ... }, 1~100 범위의 값
 *   description JSONB NOT NULL, -- 각 벡터 항목에 대한 설명 포함
 *   updated_at TIMESTAMP DEFAULT NOW()
 * );
 * 
 * -- 사용자 게임 기록 (플레이 히스토리)
 * CREATE TABLE user_game_logs (
 *   id UUID PRIMARY KEY DEFAULT uuid_generate_v4(),
 *   user_id UUID NOT NULL,
 *   game_id TEXT NOT NULL REFERENCES games(id),
 *   played_at TIMESTAMP DEFAULT NOW(),
 *   UNIQUE(user_id, game_id, played_at)
 * );
 * 
 * -- 사용자 벡터 캐시 테이블 (계산된 추천 벡터 캐싱)
 * CREATE TABLE cached_user_vectors (
 *   user_id UUID PRIMARY KEY,
 *   vector JSONB NOT NULL,
 *   updated_at TIMESTAMP DEFAULT NOW()
 * );
 * 
 * -- 트렌드 벡터 캐시 테이블 (전체 트렌드 벡터 캐싱)
 * CREATE TABLE cached_trend_vector (
 *   id TEXT PRIMARY KEY, -- 예: 'global_trend'
 *   vector JSONB NOT NULL,
 *   updated_at TIMESTAMP DEFAULT NOW()
 * );
 * 
 * -- 게임 테이블 검수 필드 추가
 * ALTER TABLE games ADD COLUMN IF NOT EXISTS isVerified BOOLEAN DEFAULT false;
 * 
 * -- 인덱스 추가
 * CREATE INDEX IF NOT EXISTS idx_user_game_logs_user_id ON user_game_logs(user_id);
 * CREATE INDEX IF NOT EXISTS idx_user_game_logs_game_id ON user_game_logs(game_id);
 * CREATE INDEX IF NOT EXISTS idx_category_vectors_category ON category_vectors(category);
 */

// --- SQL 스키마 정의 주석 ---
/**
 * SQL 스키마 정의 (Supabase SQL 에디터에서 실행)
 * 
 * -- 카테고리 벡터 테이블 (기존)
 * CREATE TABLE category_vectors ( ... );
 * 
 * -- 사용자 게임 기록 (기존)
 * CREATE TABLE user_game_logs ( ... );
 * 
 * -- 사용자 벡터 캐시 테이블 (기존)
 * CREATE TABLE cached_user_vectors ( ... );
 * 
 * -- 트렌드 벡터 캐시 테이블 (기존)
 * CREATE TABLE cached_trend_vector ( ... );
 * 
 * -- 게임 테이블 검수 필드 추가 (기존)
 * ALTER TABLE games ADD COLUMN IF NOT EXISTS isVerified BOOLEAN DEFAULT false;
 * 
 * -- 인덱스 추가 (기존)
 * CREATE INDEX IF NOT EXISTS idx_user_game_logs_user_id ON user_game_logs(user_id);
 * CREATE INDEX IF NOT EXISTS idx_user_game_logs_game_id ON user_game_logs(game_id);
 * CREATE INDEX IF NOT EXISTS idx_category_vectors_category ON category_vectors(category);
 * 
 * -- 캐시된 추천 게임 목록 테이블 (신규 추가)
 * CREATE TABLE cached_recommendations (
 *   cache_key TEXT PRIMARY KEY, -- 예: 'user:USER_ID', 'global_trend'
 *   game_ids JSONB NOT NULL, -- 추천된 게임 ID 배열 (문자열 배열)
 *   created_at TIMESTAMPTZ DEFAULT NOW(),
 *   expires_at TIMESTAMPTZ NOT NULL -- 만료 시각
 * );
 * CREATE INDEX idx_cached_recommendations_expires_at ON cached_recommendations(expires_at);
 */

// --- 캐시된 ID로 게임 조회하는 함수 (신규 추가 또는 fetchGames 수정 필요) ---
// fetchGames 함수가 ID 배열을 받아 해당 게임들을 조회하도록 수정하거나,
// 아래와 같은 별도 함수를 만듭니다. 여기서는 별도 함수로 가정합니다.
async function fetchGamesByIds(gameIds: string[], limit: number, offset: number): Promise<GameType[]> {
  if (!gameIds || gameIds.length === 0) return [];
  const supabase = createClient();
  // 캐시된 ID 목록 내에서 페이징 적용
  const paginatedIds = gameIds.slice(offset, offset + limit);
  if (paginatedIds.length === 0) return [];

  const { data, error } = await supabase
    .from('games')
    .select('*')
    .in('id', paginatedIds); // 페이징된 ID로 조회

  if (error) {
    console.error("fetchGamesByIds 오류:", error);
    return [];
  }
  // Supabase는 IN 절에서 순서를 보장하지 않으므로, 원래 ID 순서대로 정렬
  const sortedData = paginatedIds.map(id => data?.find(game => game.id === id)).filter(Boolean) as GameType[];
  return sortedData;
}


// 사용자별 게임 추천 알고리즘 (벡터 기반 + 캐싱)
export async function getPersonalizedRecommendations(
  userId: string | null = null,
  limit: number = 12,
  offset: number = 0
): Promise<GameType[]> {
  const supabase = createClient(); // Create client using utility
  console.log(`getPersonalizedRecommendations: 사용자 ${userId}, limit ${limit}, offset ${offset} 추천 시작`);

  if (!userId || userId === "Anon12") {
    console.log(`getPersonalizedRecommendations: 익명 사용자(${userId}), 트렌드 추천으로 전환합니다.`);
    return getTrendRecommendations(limit, offset); // 익명 사용자는 트렌드 추천 사용
  }

  const cacheKey = `user:${userId}`;
  const now = new Date();
  const personalCacheDurationMs = 12 * 60 * 60 * 1000; // 12시간

  try {
    // 1. 캐시 확인
    console.log(`getPersonalizedRecommendations: 사용자 ${userId} 추천 캐시 확인 중...`);
    const { data: cachedData, error: cacheCheckError } = await supabase
      .from("cached_recommendations")
      .select("game_ids, expires_at")
      .eq("cache_key", cacheKey)
      .maybeSingle(); // single 대신 maybeSingle 사용 권장

    if (cacheCheckError) {
       // PGRST116 (No rows found) is expected, other errors should be logged
      if (cacheCheckError.code !== 'PGRST116') {
        console.error(`getPersonalizedRecommendations: 캐시 조회 오류 (${userId}):`, cacheCheckError);
      }
      // 오류 발생 시 캐시 무시하고 계속 진행 (PGRST116 포함)
    } else if (cachedData && new Date(cachedData.expires_at) > now) {
        console.log(`getPersonalizedRecommendations: 사용자 ${userId} 유효한 추천 캐시 사용.`);
        const cachedGameIds = cachedData.game_ids as string[];
        // 캐시된 ID 목록으로 게임 데이터 조회 (페이징 적용)
        return fetchGamesByIds(cachedGameIds, limit, offset);
    }


    console.log(`getPersonalizedRecommendations: 사용자 ${userId} 추천 캐시 없거나 만료됨. 추천 목록 계산 시작...`);

    // --- 캐시 없으면 추천 로직 실행 (기존 로직과 유사) ---
    // 2. 사용자 벡터 가져오기 (캐시된 벡터 확인)
    let userVector = await getUserVector(userId); // getUserVector 헬퍼 함수 사용 (기존 로직 캡슐화)

    if (!userVector) {
      console.log(`getPersonalizedRecommendations: 사용자 ${userId} 벡터 없음. 인기 게임 반환.`);
      // 사용자 벡터가 없으면 캐시하지 않고 인기 게임 반환
      return getPopularGames(limit, offset);
    }

    // 3. 모든 게임 가져오기 (개선: 필요한 게임만 효율적으로 가져오도록 고려)
    // 주의: 성능을 위해 모든 게임을 가져오는 대신, 후보군을 줄이는 전략 고려 필요
    console.log(`getPersonalizedRecommendations: 추천 대상 게임 목록 조회 중...`);
    const allGames = await fetchGames(200); // 예시: 후보군 늘림 (실제로는 더 최적화 필요)
    if (!allGames || allGames.length === 0) {
       console.warn(`getPersonalizedRecommendations: 추천 대상 게임 목록을 가져올 수 없습니다.`);
       return [];
    }
     console.log(`getPersonalizedRecommendations: 추천 대상 게임 ${allGames.length}개 조회 완료.`);
      
    // 4. 유사도 계산 및 정렬
    console.log(`getPersonalizedRecommendations: 사용자 ${userId} 벡터와 게임 벡터 간 유사도 계산 중...`);
    const scoredGames = await Promise.all(
      allGames.map(async (game) => {
        const gameVector = await calculateGameVector(game);
        if (!gameVector) return { game, score: -Infinity }; // 점수 최소화하여 제외
        const similarityScore = calculateVectorSimilarity(userVector!, gameVector);
        const popularityBoost = (game.views || 0) * 0.00001;
        const newBoost = game.isNew ? 0.2 : 0;
        const randomFactor = Math.random() * 0.1 - 0.05; // 난수 영향 줄임 (0.05 ~ -0.05)
        return { game, score: similarityScore + popularityBoost + newBoost + randomFactor };
      })
    );

    // 필터링 및 정렬 (limit, offset 적용 전 전체 목록)
    const sortedGamesFull = scoredGames
      .filter(item => item.score > -Infinity) // 계산 실패 게임 제외
      .sort((a, b) => b.score - a.score)
      .map(item => item.game);

    // 5. 결과 캐싱
    const gameIdsToCache = sortedGamesFull.map(game => game.id);
    if (gameIdsToCache.length > 0) {
        const expiresAt = new Date(now.getTime() + personalCacheDurationMs);
        console.log(`getPersonalizedRecommendations: 사용자 ${userId} 추천 결과 캐싱 시도 (${gameIdsToCache.length}개 게임 ID). 만료: ${expiresAt.toISOString()}`);
        const { error: cacheUpsertError } = await supabase
            .from("cached_recommendations")
            .upsert({
                cache_key: cacheKey,
                game_ids: gameIdsToCache, // Store as JSON array of strings
                expires_at: expiresAt.toISOString(),
                created_at: now.toISOString() // 명시적 생성 시간
            });
        if (cacheUpsertError) {
            console.error(`getPersonalizedRecommendations: 추천 캐시 저장 오류 (${userId}):`, cacheUpsertError);
        } else {
            console.log(`getPersonalizedRecommendations: 사용자 ${userId} 추천 결과 캐싱 완료.`);
        }
    }

    // 6. 페이징 적용 후 반환
    const finalResult = sortedGamesFull.slice(offset, offset + limit);
    console.log(`getPersonalizedRecommendations: 사용자 ${userId} 계산된 추천 게임 ${finalResult.length}개 반환.`);
    return finalResult;

  } catch (error) {
    console.error(`getPersonalizedRecommendations (${userId}) 처리 중 예외 발생:`, error);
    // 예외 발생 시 캐시하지 않고 인기 게임 반환
    return getPopularGames(limit, offset);
  }
}

// 트렌드 기반 추천 (캐싱 추가)
export async function getTrendRecommendations(
  limit: number = 12, 
  offset: number = 0
): Promise<GameType[]> {
  const supabase = createClient(); // Create client using utility
  const cacheKey = "global_trend";
  const now = new Date();
  const trendCacheDurationMs = 24 * 60 * 60 * 1000; // 24시간

  try {
    // --- 캐시 없으면 추천 로직 실행 (기존 로직과 유사) ---
    // 2. 트렌드 벡터 가져오기 (기존 벡터 캐시 로직 활용)
    let trendVector = await getTrendVector(); // getTrendVector 헬퍼 함수 사용 (기존 로직 캡슐화)
    
    if (!trendVector) {
        console.log("getTrendRecommendations: 트렌드 벡터 없음. 인기 게임 반환.");
        return getPopularGames(limit, offset); // 트렌드 벡터 없으면 캐시하지 않음
    }
    
    // 3. 모든 게임 가져오기 (개선 필요)
    console.log("getTrendRecommendations: 추천 대상 게임 목록 조회 중...");
    const allGames = await fetchGames(200); // 예시: 후보군 늘림
    if (!allGames || allGames.length === 0) {
        console.warn("getTrendRecommendations: 추천 대상 게임 목록을 가져올 수 없습니다.");
        return [];
    }
     console.log(`getTrendRecommendations: 추천 대상 게임 ${allGames.length}개 조회 완료.`);
      
    // 4. 유사도 계산 및 정렬
     console.log("getTrendRecommendations: 트렌드 벡터와 게임 벡터 간 유사도 계산 중...");
    const scoredGames = await Promise.all(
      allGames.map(async (game) => {
        const gameVector = await calculateGameVector(game);
        if (!gameVector) return { game, score: -Infinity }; // 점수 최소화하여 제외
        
        const similarityScore = calculateVectorSimilarity(trendVector!, gameVector);
        
        // 게임 최신성 및 인기도에 따른 추가 가중치
        const recencyBoost = game.isNew ? 0.2 : 0; // 기존 로직
        const featuredBoost = game.isFeatured ? 0.15 : 0; // 기존 로직
        const randomFactor = Math.random() * 0.1 - 0.05; // 난수 영향 줄임
        
        return { 
          game, 
          score: similarityScore + recencyBoost + featuredBoost + randomFactor 
        };
      })
    );
    
    // 필터링 및 정렬 (limit, offset 적용 전 전체 목록)
    const sortedGamesFull = scoredGames
      .filter(item => item.score > -Infinity) // 계산 실패 게임 제외
      .sort((a, b) => b.score - a.score)
      .map(item => item.game);
    // 6. 페이징 적용 후 반환
    const finalResult = sortedGamesFull.slice(offset, offset + limit);
    console.log(`getTrendRecommendations: 계산된 트렌드 추천 게임 ${finalResult.length}개 반환.`);
    return finalResult;

  } catch (error) {
    console.error("트렌드 추천 처리 중 예외 발생:", error);
    // 예외 발생 시 캐시하지 않고 인기 게임 반환
    return getPopularGames(limit, offset);
  }
}

// --- 헬퍼 함수들 (기존 로직 캡슐화) ---

// 기본 벡터 구조 반환 함수 (타입 체크 및 기본값 설정에 사용)
function defaultVector(): Vector {
    return {
        speed: 50, mood: 50, complexity: 50, male_focus: 50, female_focus: 50,
        casualness: 50, playtime: 50, visual: 50, theme: 50, depth: 50
    };
}


// 사용자 벡터 가져오기 (캐시 확인 및 계산)
async function getUserVector(userId: string): Promise<Vector | null> {
    const supabase = createClient();
    const userVector = await calculateUserVector(userId); // 실제 계산 로직 호출

    // 3. 계산된 벡터 캐싱
    if (userVector) {
      console.log(`getUserVector: 사용자 ${userId} 벡터 계산 완료. 캐싱 시도...`);
      const { error: upsertError } = await supabase
        .from("cached_user_vectors")
        .upsert({
          user_id: userId,
          vector: userVector,
          updated_at: new Date().toISOString()
        });
      if (upsertError) {
         console.error(`getUserVector: 사용자 ${userId} 벡터 캐싱 오류:`, upsertError);
      }
      return userVector;
    } else {
        console.warn(`getUserVector: 사용자 ${userId} 벡터 계산 실패.`);
        return null;
    }
}

// 트렌드 벡터 가져오기 (캐시 확인 및 계산)
async function getTrendVector(): Promise<Vector | null> {
    const supabase = createClient();
    const trendVector = await calculateTrendVector(); // 실제 계산 로직 호출

    // 3. 계산된 벡터 캐싱
    if (trendVector) {
      console.log("getTrendVector: 트렌드 벡터 계산 완료. 캐싱 시도...");
      const { error: upsertError } = await supabase
        .from("cached_trend_vector")
        .upsert({
          id: "global_trend",
          vector: trendVector,
          updated_at: new Date().toISOString()
        });
        if (upsertError) {
            console.error("getTrendVector: 트렌드 벡터 캐싱 오류:", upsertError);
        }
       return trendVector;
    } else {
        console.warn("getTrendVector: 트렌드 벡터 계산 실패.");
        return null;
    }
}
async function calculateUserVector(userId: string): Promise<Vector | null> {
  const supabase = createClient(); // Create client using utility
  try {
    // 1. 사용자의 플레이 기록 가져오기
    const { data: userGameLogs, error: logsError } = await supabase
      .from("user_game_logs")
      .select("game_id")
      .eq("user_id", userId)
      .order("played_at", { ascending: false })
      .limit(50); // 최근 50개 게임으로 늘림 (조절 가능)

    if (logsError) {
      console.error(`calculateUserVector 오류 (플레이 기록 조회 ${userId}):`, logsError);
      return null;
    }
    if (!userGameLogs || userGameLogs.length === 0) {
      // console.warn(`calculateUserVector: 사용자 ${userId} 플레이 기록 없음.`); // 정보성 로그
      return null; // 플레이 기록 없으면 벡터 계산 불가
    }
    const gameIds = userGameLogs.map(log => log.game_id);

    // 2. 플레이한 게임들의 카테고리 정보 가져오기
    const { data: games, error: gamesError } = await supabase
      .from("games")
      .select("id, categories") // Select id and categories
      .in("id", gameIds);

    if (gamesError) {
      console.error(`calculateUserVector 오류 (게임 카테고리 조회 ${userId}):`, gamesError);
      return null;
    }
    if (!games || games.length === 0) {
      // console.warn(`calculateUserVector: 사용자 ${userId}가 플레이한 게임 정보 없음.`);
       return null;
    }

    // 3. 모든 카테고리 수집 및 고유 카테고리 추출
    const allCategories = games.flatMap(game => getGameCategories(game.categories));
    if (allCategories.length === 0) {
      // console.warn(`calculateUserVector: 사용자 ${userId} 플레이 게임 유효 카테고리 없음.`);
      return null;
    }
    const uniqueCategories = Array.from(new Set(allCategories));

    // 4. 각 카테고리의 벡터값 가져오기
    const { data: categoryVectors, error: vectorsError } = await supabase
      .from("category_vectors")
      .select("category, vector") // Select category and vector
      .in("category", uniqueCategories);

    if (vectorsError) {
      console.error(`calculateUserVector 오류 (카테고리 벡터 조회 ${userId}):`, vectorsError);
      return null;
    }
    if (!categoryVectors || categoryVectors.length === 0) {
      // console.warn(`calculateUserVector: 사용자 ${userId} 플레이 기반 카테고리 벡터 없음.`);
      return null; // 관련 카테고리 벡터 없으면 계산 불가
    }

    // 5. 벡터 평균 계산 (기본값 50 사용, 초기화 개선)
    const vectorSum: Partial<Vector> = {}; // Initialize empty
    let vectorCount = 0;
    const vectorKeys = Object.keys(defaultVector()) as Array<keyof Vector>; 
    
    // Initialize sum vector with zeros
    vectorKeys.forEach(key => { vectorSum[key] = 0; });

    categoryVectors.forEach(cv => {
      // Ensure cv.vector is a valid object and contains expected keys (or use defaults)
      if (cv.vector && typeof cv.vector === 'object') {
        const currentVec = cv.vector as Partial<Vector>;
        vectorKeys.forEach(key => {
          // Add the value or default (50) if the key is missing in cv.vector
          vectorSum[key]! += (typeof currentVec[key] === 'number' ? currentVec[key]! : 50);
        });
        vectorCount++;
      } else {
         // console.warn(`calculateUserVector: 카테고리 '${cv.category}' 벡터 유효하지 않음 (${userId}).`);
         // Optionally, handle this case, e.g., skip or use a default vector for the category
      }
    });
    
    // 평균값으로 변환
    if (vectorCount > 0) {
       vectorKeys.forEach(key => {
        vectorSum[key] = vectorSum[key]! / vectorCount;
      });
      // console.log(`calculateUserVector: 사용자 ${userId} 벡터 계산 완료.`); // Logged in getUserVector
      return vectorSum as Vector;
    }
    
    // console.warn(`calculateUserVector: 사용자 ${userId} 벡터 계산 불가 (유효 카테고리 벡터 없음).`);
    return null; // No valid category vectors found for averaging
  } catch (error) {
    console.error(`calculateUserVector (${userId}) 계산 중 예외 발생:`, error);
    return null;
  }
}

// --- calculateGameVector 수정 (벡터 초기화 개선) ---
async function calculateGameVector(game: GameType): Promise<Vector | null> {
  const supabase = createClient(); // Create client using utility
  try {
    // 1. 게임 카테고리 확인
    const categories = getGameCategories(game.categories);
    if (categories.length === 0) {
      // console.warn(`calculateGameVector: 게임 ${game.id} 카테고리 없음.`);
      return null; // No categories, cannot calculate vector
    }
    
    // 2. 카테고리 벡터 가져오기
    const { data: categoryVectors, error } = await supabase
      .from("category_vectors")
      .select("vector") // Only select the vector
      .in("category", categories);
    
    if (error) {
      console.error(`calculateGameVector 오류 (카테고리 벡터 조회, 게임 ${game.id}):`, error);
      return null;
    }
    if (!categoryVectors || categoryVectors.length === 0) {
      // console.warn(`calculateGameVector: 게임 ${game.id} 관련 카테고리 벡터 없음.`);
      return null; // No relevant category vectors found
    }
    
    // 3. 벡터 평균 계산
    const vectorSum: Partial<Vector> = {};
    let vectorCount = 0;
    const vectorKeys = Object.keys(defaultVector()) as Array<keyof Vector>;

    // Initialize sum vector with zeros
    vectorKeys.forEach(key => { vectorSum[key] = 0; });
    
    categoryVectors.forEach(cv => {
       if (cv.vector && typeof cv.vector === 'object') {
         const currentVec = cv.vector as Partial<Vector>;
        vectorKeys.forEach(key => {
          vectorSum[key]! += (typeof currentVec[key] === 'number' ? currentVec[key]! : 50);
        });
        vectorCount++;
      }
    });
    
    // 평균값으로 변환
    if (vectorCount > 0) {
      vectorKeys.forEach(key => {
        vectorSum[key] = vectorSum[key]! / vectorCount;
      });
      return vectorSum as Vector;
    }
    
    // console.warn(`calculateGameVector: 게임 ${game.id} 벡터 계산 불가 (유효 카테고리 벡터 없음).`);
    return null; // Should not happen if categoryVectors.length > 0, but safety check
  } catch (error) {
    console.error(`calculateGameVector (게임 ${game.id}) 계산 중 예외 발생:`, error);
    return null;
  }
}

// --- calculateTrendVector 수정 (벡터 초기화 개선) ---
async function calculateTrendVector(): Promise<Vector | null> {
  const supabase = createClient(); // Create client using utility
  try {
    // 1. 인기 상위 게임 가져오기 (현재: plays 기준)
    const { data: popularGames, error: gamesError } = await supabase
      .from("games")
      .select("id, categories") // Select id and categories
      .order("plays", { ascending: false })
      .limit(100); // 기준: 플레이 수 상위 100개
    
    if (gamesError) {
      console.error("calculateTrendVector 오류 (인기 게임 조회):", gamesError);
      return null;
    }    
    if (!popularGames || popularGames.length === 0) {
      // console.warn("calculateTrendVector: 인기 게임 없음.");
      return null; // No popular games to base trend on
    }

    // 2. 모든 카테고리 수집 및 고유 카테고리 추출
    const allCategories = popularGames.flatMap(game => getGameCategories(game.categories));
    if (allCategories.length === 0) {
      // console.warn("calculateTrendVector: 인기 게임 유효 카테고리 없음.");
      return null; // No categories found in popular games
    }
    const uniqueCategories = Array.from(new Set(allCategories));

    // 3. 각 카테고리의 벡터값 가져오기
    const { data: categoryVectors, error: vectorsError } = await supabase
      .from("category_vectors")
      .select("category, vector") // Select category and vector
      .in("category", uniqueCategories);
    
    if (vectorsError) {
       console.error("calculateTrendVector 오류 (카테고리 벡터 조회):", vectorsError);
      return null;
    }
    if (!categoryVectors || categoryVectors.length === 0) {
      // console.warn("calculateTrendVector: 조회된 카테고리 벡터 없음.");
      return null; // No vectors found for the relevant categories
    }

    // 4. 벡터 평균 계산
    const vectorSum: Partial<Vector> = {};
    let vectorCount = 0;
    const vectorKeys = Object.keys(defaultVector()) as Array<keyof Vector>;

    // Initialize sum vector with zeros
    vectorKeys.forEach(key => { vectorSum[key] = 0; });
    
    categoryVectors.forEach(cv => {
      if (cv.vector && typeof cv.vector === 'object') { 
         const currentVec = cv.vector as Partial<Vector>;
        vectorKeys.forEach(key => {
          vectorSum[key]! += (typeof currentVec[key] === 'number' ? currentVec[key]! : 50);
        });
        vectorCount++;
      } else {
        // console.warn(`calculateTrendVector: 카테고리 '${cv.category}' 벡터 유효하지 않음.`);
      }
    });
    
    // 평균값으로 변환
    if (vectorCount > 0) {
      vectorKeys.forEach(key => {
        vectorSum[key] = vectorSum[key]! / vectorCount;
      });
      // console.log("calculateTrendVector: 트렌드 벡터 계산 완료."); // Logged in getTrendVector
      return vectorSum as Vector;
    }
    
    // console.warn("calculateTrendVector: 유효 카테고리 벡터 없어 평균 계산 불가.");
    return null; // No valid vectors to average
  } catch (error) {
    console.error("calculateTrendVector 계산 중 예외 발생:", error);
    return null;
  }
}


// 벡터 유사도 계산 (코사인 유사도, NaN 방지 강화)
function calculateVectorSimilarity(vector1: Vector, vector2: Vector): number {
  let dotProduct = 0;
  let magnitude1Sq = 0; // Squared magnitude to avoid sqrt until the end
  let magnitude2Sq = 0;
  const vectorKeys = Object.keys(defaultVector()) as Array<keyof Vector>; // Use default keys

  vectorKeys.forEach(key => {
    // Use default 50 if a key is missing in either vector (should not happen with proper calculation)
    const v1 = typeof vector1[key] === 'number' ? vector1[key] : 50;
    const v2 = typeof vector2[key] === 'number' ? vector2[key] : 50;
    
    dotProduct += v1 * v2;
    magnitude1Sq += v1 * v1;
    magnitude2Sq += v2 * v2;
  });
  
  // Avoid division by zero or sqrt of zero
  if (magnitude1Sq === 0 || magnitude2Sq === 0) {
    return 0;
  }
  
  const magnitude1 = Math.sqrt(magnitude1Sq);
  const magnitude2 = Math.sqrt(magnitude2Sq);
  
  const similarity = dotProduct / (magnitude1 * magnitude2);
  
  // Ensure result is a valid number between -1 and 1 (due to potential floating point issues)
  return isNaN(similarity) ? 0 : Math.max(-1, Math.min(1, similarity));
}


// 인기 게임 목록 (조회수 기준) - 기존 로직 유지 (StrictMode 로깅 정리)
export async function getPopularGames(
  limit: number = 12, 
  offset: number = 0
): Promise<GameType[]> {
  const supabase = createClient(); // Create client using utility
  // console.log(`getPopularGames: limit ${limit}, offset ${offset} 조회 시작`); // 정보성 로그 줄임

  let isStrictModeEnabled = false; // 기본값: Strict Mode 비활성화
  try {
    const { data: settingData, error: settingError } = await supabase
      .from('app_settings')
      .select('value')
      .eq('key', 'strictMode')
      .maybeSingle(); // 설정이 없을 수도 있으므로 maybeSingle 사용

    if (settingError && settingError.code !== 'PGRST116') { // Ignore 'not found' error
      console.error("getPopularGames: StrictMode 설정 조회 오류:", settingError);
    } else if (settingData) {
      isStrictModeEnabled = settingData.value === 'true';
    }
    // console.log(`getPopularGames: StrictMode 활성화 상태: ${isStrictModeEnabled}`);

  } catch (settingCatchError) {
    console.error("getPopularGames: StrictMode 설정 조회 중 예외 발생:", settingCatchError);
  }

  try {
    // 기본 쿼리 빌더 생성
    let query = supabase
      .from("games")
      .select("*", { count: 'exact' })
      .order("plays", { ascending: false }) // 'views' 대신 'plays' 사용 확인 필요
      .range(offset, offset + limit - 1);

    // Strict Mode가 활성화된 경우에만 isVerified 필터 추가
    if (isStrictModeEnabled) {
      // console.log("getPopularGames: StrictMode 활성화됨, isVerified=true 필터 적용");
      query = query.eq("isVerified", true);
    } else {
      // console.log("getPopularGames: StrictMode 비활성화됨, isVerified 필터 미적용");
    }

    // 쿼리 실행
    const { data, error, count } = await query;

    if (error) { 
      console.error("getPopularGames 오류:", error);
      throw error; // Re-throw error to be potentially caught upstream
    }
    
    // console.log(`getPopularGames: 조회 결과 ${data?.length ?? 0}개 게임 반환 (총 count ${isStrictModeEnabled ? '(verified)' : '(all)'}: ${count ?? 'N/A'})`);
    return data || [];
  } catch (error) {
     // Already logged in the inner try block if it originated there
    // console.error("인기 게임 가져오기 처리 중 예외 발생:", error); // Avoid double logging
    return []; // Return empty array on error
  }
}


// 비슷한 게임 추천 (특정 게임과 유사한 게임) - 기존 로직 유지 (약간 개선)
export async function getSimilarGames(
  gameId: string, 
  limit: number = 4
): Promise<GameType[]> {
  const supabase = createClient(); // Create client using utility
  let currentGame: GameType | null = null; // Define currentGame here for broader scope

  try {
    // 1. 현재 게임 정보 가져오기
    const { data: gameData, error: currentGameError } = await supabase
      .from("games")
      .select("*") // 필요한 필드만 선택하는 것이 좋음 (id, categories, isVerified, plays)
      .eq("id", gameId)
      .maybeSingle(); 

    if (currentGameError) throw currentGameError;
    if (!gameData) {
      console.warn(`getSimilarGames: 게임 ID ${gameId} 찾을 수 없음.`);
      return [];
    }
    currentGame = gameData; // Assign to the broader scope variable

    // 2. 현재 게임의 벡터 계산
    const currentGameVector = await calculateGameVector(currentGame!);

    // 3. 후보 게임 조회 (isVerified 필터링)
    // 성능 개선: 모든 게임 대신 관련성 높은 후보군 조회 (예: 동일 카테고리 게임)
    const { data: candidateGames, error: gamesError } = await supabase
      .from("games")
      .select("*") // 필요한 필드만 선택
      .neq("id", gameId)
      .eq("isVerified", true) // 검증된 게임만 대상
      .limit(200); // 후보군 제한 (조절 가능)

    if (gamesError) throw gamesError;
    if (!candidateGames || candidateGames.length === 0) {
       // console.warn(`getSimilarGames: 유사 게임 후보 없음 (ID: ${gameId}).`);
       return [];
    }

    // 4. 유사도 계산
    const scoredGames = await Promise.all(
      candidateGames.map(async (game) => {
         let score = 0;
         const gameVector = await calculateGameVector(game);

         // 벡터 기반 유사도 (currentGameVector가 있을 때만 계산)
         if (currentGameVector && gameVector) {
             score += calculateVectorSimilarity(currentGameVector, gameVector);
         }

         // 카테고리 일치 보너스
         const currentGameCategories = getGameCategories(currentGame!.categories); // Use non-null assertion
         const compareGameCategories = getGameCategories(game.categories);
         if (currentGameCategories.length > 0 && compareGameCategories.length > 0) {
             const commonCategories = compareGameCategories.filter(c => currentGameCategories.includes(c));
             score += commonCategories.length * 0.1; // 보너스 가중치 조정 가능
         }

         // 인기도 약간 반영 (옵션)
         // score += (game.plays || 0) * 0.000001;

         return { game, score };
      })
    );
    
    // 5. 점수로 정렬하고 반환
    return scoredGames
      .sort((a, b) => b.score - a.score)
      .slice(0, limit)
      .map(item => item.game);

  } catch (error) {
    console.error(`유사 게임 추천 오류 (ID: ${gameId}):`, error);
    // 오류 발생 시 대체 로직 (예: 카테고리 기반)
    // currentGame이 정의되었는지 확인 후 호출
    if (currentGame) {
        // console.log(`유사 게임 오류 발생, 카테고리 기반 대체 실행 (ID: ${gameId})`);
        return getRelatedGamesByCategory(currentGame, limit);
    }
    return []; // currentGame 정보가 없으면 빈 배열 반환
  }
}


// 카테고리 기반 간단 관련 게임 조회 (getSimilarGames의 대체용) - 로직 유지 (약간 개선)
async function getRelatedGamesByCategory(
  game: GameType, 
  limit: number
): Promise<GameType[]> {
  const supabase = createClient(); // Create client using utility
  try {
    const categories = getGameCategories(game.categories);
    
    if (categories.length === 0) {
        // console.warn(`getRelatedGamesByCategory: 게임 ${game.id} 카테고리 없음, 인기 게임 반환.`);
        return getPopularGames(limit); 
    }
    
    // 관련 카테고리 게임 조회 (성능 고려: 모든 게임 조회 대신 필터링)
    // RPC 함수 사용 또는 클라이언트 측 필터링 (현재 구현)
     const { data, error } = await supabase
      .from("games")
      .select("*") // 필요한 필드만 (id, categories, plays, isVerified)
      .neq("id", game.id)
      .eq("isVerified", true)
      .order("plays", { ascending: false }) // 인기도 높은 순 정렬
      .limit(100); // 후보군 제한 (조절 가능)

     if (error) throw error;
     if (!data || data.length === 0) {
        // console.warn(`getRelatedGamesByCategory: 관련 카테고리 게임 후보 없음 (ID: ${game.id}), 인기 게임 반환.`);
        return getPopularGames(limit);
     }

    // 카테고리 일치 스코어링
    const scoredGames = data
        .map(g => {
            const gameCategories = getGameCategories(g.categories);
            const commonCategories = gameCategories.filter(c => categories.includes(c));
            // 점수: 공통 카테고리 수 + 인기도 약간 반영
             const score = commonCategories.length + (g.plays || 0) * 0.00001; 
             return { game: g, score, hasCommon: commonCategories.length > 0 };
        })
        .filter(item => item.hasCommon); // 공통 카테고리가 있는 게임만 필터링
        
    if (scoredGames.length === 0) {
        // console.warn(`getRelatedGamesByCategory: 공통 카테고리 게임 없음 (ID: ${game.id}), 인기 게임 반환.`);
        return getPopularGames(limit); // 공통 카테고리 게임 없으면 인기 게임 반환
    }

    return scoredGames
      .sort((a, b) => b.score - a.score)
      .slice(0, limit)
      .map(item => item.game);

  } catch (error) {
    console.error(`카테고리 기반 관련 게임 조회 오류 (ID: ${game.id}):`, error);
    return getPopularGames(limit); // 오류 시 인기 게임 반환
  }
} 